<template>
  <div class="auth-page signup-email-page">
    <div class="auth-page__fields">
      <div class="min-h-[37px] lg:min-h-[56px]">
        <UIButton
          v-if="isQuestionnaireUpdate ? currentIndex > 0 : true"
          id="back-button"
          left-icon="back"
          class="auth-page__back"
          variant="ghost"
          @click="handleBack"
        >
          Back
        </UIButton>
      </div>
      <form class="signup-email-page__form">
        <QuestionnaireProgressBar :current-index="currentIndex" :length="STEPS_LENGTH" />
        <QuestionnaireForm
          :loading="isPending || isQuestionnairePending"
          :index="currentIndex"
          @next="onSubmit"
          @register="register"
        />
      </form>
    </div>
    <div ref="parentRef" class="auth-page__img">
      <img v-if="currentIndex === 0" width="465" height="559" src="/images/main-page-image.png" />
      <img
        v-if="currentIndex === 1 || currentIndex === 2"
        width="521"
        height="781"
        class="signup-email-page__image-full-size"
        src="/images/questionnaire-image-1.png"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
/* eslint-disable camelcase */
import { useForm } from 'vee-validate'
import { SITE_NAME } from '~/constants'
import type { RegisterVerifyEmailDTO } from '~/schemas/auth/register'
import { stepOneTypedSchema, stepThreeTypedSchema, stepTwoTypedSchema } from '~/schemas/questionnaire'
import type { QuestionnaireSchema } from '~/schemas/questionnaire'
import useAuthStore from '~/store/auth'
import { isThriveStateDomain } from '~/guards'

const STEPS_LENGTH = 3

useHead({
  title: `${SITE_NAME} | Sign up`,
})

definePageMeta({
  layout: 'auth',
})

const { isSuperAdmin, isCEO, isPartnerManager, isAuth } = storeToRefs(useAuthStore())

const { data: user } = useCurrentUser(isAuth.value)

watch(
  () => user.value?.is_questionnaire_filled,
  value => {
    if (!isAuth.value) {
      return
    }

    if (
      value ||
      isThriveStateDomain(user.value?.email) ||
      isCEO.value ||
      isSuperAdmin.value ||
      isPartnerManager.value
    ) {
      navigateTo({
        name: 'dashboard',
      })
    }
  },
  {
    immediate: true,
    deep: true,
  },
)

const currentIndex = ref(0)

const { isMobile } = useAppBreakpoints()

const currentSchema = computed(() => {
  return [stepOneTypedSchema, stepTwoTypedSchema(isMobile), stepThreeTypedSchema(isMobile)][currentIndex.value]
})

const route = useRoute()

const { handleSubmit, values, setValues } = useForm<QuestionnaireSchema>({
  validationSchema: currentSchema,
  keepValuesOnUnmount: true,
  initialValues: {
    email: route.query.email as string,
  },
})

const { data: countries } = useCountries()

watch(
  () => values.country,
  value => {
    const selectedCountry = countries.value?.find(({ id }) => id === value)

    const selectedCountryFormatted = selectedCountry
      ? {
          ...selectedCountry,
          phone_code: `+${selectedCountry?.phone_code}`,
        }
      : undefined

    setValues(
      {
        ...values,
        phone: {
          ...values.phone,
          country: selectedCountryFormatted,
        },
      },
      false,
    )
  },
)

const onSubmit = handleSubmit(() => {
  currentIndex.value++
})

const { catchErrors } = useErrorHandler()

const { mutateAsync: registerUser, isPending } = useRegister()

const { mutateAsync: updateUserQuestionnaire, isPending: isQuestionnairePending } = useQuestionnaire()

const isQuestionnaireUpdate = computed(() => {
  return Boolean(route.query.email)
})

const register = handleSubmit(() => {
  const {
    email,
    phone,
    name,
    country,
    countries,
    languages,
    source_of_knowledge,
    social_network,
    preferred_messenger,
    messenger_username,
    other_details,
    refer,
  } = values

  const questionnaire = {
    refer,
    countries,
    languages: [languages],
    source_of_knowledge,
    social_network,
    preferred_messenger,
    other_details,
    messenger_username,
  }

  const payload = {
    user: {
      email,
      name,
      phone_country_id: phone!.country.id,
      phone: phone!.number,
      country,
    },

    // TODO: refactor
    ...(isQuestionnaireUpdate.value
      ? questionnaire
      : {
          questionnaire,
        }),
  } as RegisterVerifyEmailDTO

  if (isQuestionnaireUpdate.value) {
    updateUserQuestionnaire(payload).catch(catchErrors)

    return
  }

  registerUser(payload).catch(catchErrors)
})

const { back } = useRouter()

const handleBack = () => {
  if (currentIndex.value > 0) {
    currentIndex.value--

    return
  }

  back()
}

const [parentRef] = useAutoAnimate()
</script>

<style scoped lang="scss">
@import '@/styles/variables.scss';
@import '@/styles/mixins.scss';

@include baseAuthPage();

.auth-page {
  &__fields {
    padding-top: 4px;
    margin-bottom: 0;

    @media screen and (max-width: 1023px) {
      padding-top: 0;
    }
  }

  &__back {
    margin-bottom: 32px;

    @media screen and (max-width: 1023px) {
      margin-bottom: 12px;
      font-size: 14px;

      :deep() {
        .icon {
          width: 20px;
          height: 20px;
        }
      }
    }
  }

  .signup-email-page {
    &__form {
      display: flex;
      flex-direction: column;
      gap: 32px;

      @media screen and (max-width: 1023px) {
        gap: 12px;
      }
    }
  }
}
</style>
